// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { CreateAssemblyFormData } from '@/assembly/components/create-assembly-dialog';
import { NumberTextField } from '@/components/number-text-field';
import {
  DeleteOutlined as DeleteIcon,
  InventoryOutlined,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { InventoryEntry, getCountAtLocation } from '@/inventory/api/types';
import { ItemDetail } from '@/items/api/types';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { Control, Controller, useWatch } from 'react-hook-form';
import { NumberFormatValues } from 'react-number-format';

/**
 * Local interface for the inventory at the selected location's site
 * The location is the selected site or zone, and each bulk item lists the site's locations
 */
interface LocationInventoryItem {
  location: SparseLocation;
  items: {
    bulkItems: {
      item: SparseItem;
      inventory: InventoryEntry[];
    }[];
  };
}

interface Props {
  /**
   * Index of the part in the form array
   */
  index: number;

  /**
   * Control from react-hook-form
   */
  control: Control<CreateAssemblyFormData>;

  /**
   * Selected parts to exclude from search
   */
  selectedParts: SparseItem[];

  /**
   * Inventory data for the selected location
   */
  inventoryData?: LocationInventoryItem;

  /**
   * Function to remove the part from the form
   */
  onRemove: () => void;

  /**
   * Whether to disable delete button
   */
  isDisableDelete: boolean;

  selectedItem: ItemDetail | null;
}

function AvailableQuantityLabel(props: {
  inventoryData: LocationInventoryItem;
  partId: string | undefined;
}) {
  const found = props.inventoryData.items.bulkItems.find(
    (p) => p.item.id === props.partId
  );
  const availableQty = found
    ? getCountAtLocation(found.inventory, props.inventoryData.location.id)
    : 0;
  return (
    <Typography variant="body2">
      Available: {formatNumber(availableQty)}{' '}
    </Typography>
  );
}

/**
 * Component for rendering a single part in an assembly BOM.
 * Supports part selection, quantity input, and serial number input when serial
 * fields should render.
 */
export function CreateAssemblyBomPart({
  index,
  control,
  inventoryData,
  onRemove,
  isDisableDelete,
  selectedItem,
}: Props) {
  const currentPart = useWatch({
    control,
    name: `parts.${index}.part`,
  });
  const theme = useTheme();
  const currentQuantity = useWatch({
    control,
    name: `parts.${index}.quantity`,
  });

  const expectedQuantity = selectedItem?.bom?.find(
    (bomItem) => bomItem.item.id === currentPart.id
  )?.quantity;

  const isOnItemBOM = Boolean(
    selectedItem?.bom?.find((bomItem) => bomItem.item.id === currentPart.id)
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Tooltip
            arrow
            title={isOnItemBOM ? 'On BOM' : 'Not on BOM'}
            placement="bottom-start"
          >
            <Stack direction={'row'} alignItems={'center'}>
              <InventoryOutlined
                fontSize="small"
                htmlColor={
                  isOnItemBOM ? theme.palette.primary.main : grey['600']
                }
                sx={{
                  mr: 1.5,
                }}
              />

              <Stack>
                <Typography variant={'subtitle1'}>{currentPart.sku}</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  {currentPart.name}
                </Typography>
                {inventoryData && (
                  <AvailableQuantityLabel
                    inventoryData={inventoryData}
                    partId={currentPart?.id}
                  />
                )}
              </Stack>
            </Stack>
          </Tooltip>
        </Grid>
        <Grid item sm={5}>
          <Controller
            control={control}
            name={`parts.${index}.quantity`}
            render={({ field: quantityField, fieldState }) => (
              <Stack>
                <NumberTextField
                  {...quantityField}
                  label="Quantity"
                  size="small"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error
                      ? fieldState.error.message
                      : expectedQuantity != currentQuantity
                        ? `BOM quantity: ${expectedQuantity ?? 0}`
                        : undefined
                  }
                  fullWidth
                  allowNegative={false}
                  value={
                    quantityField.value === null ? '' : quantityField.value
                  }
                  onChange={(value: NumberFormatValues) => {
                    quantityField.onChange(
                      value.value == '' ? null : value.floatValue
                    );
                  }}
                />
              </Stack>
            )}
          />
        </Grid>
        <Grid item sm={1}>
          <IconButton
            onClick={onRemove}
            size="small"
            sx={{ mt: 0.5 }}
            disabled={isDisableDelete}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      {currentPart?.shouldRenderSerialFields && currentQuantity && (
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <Typography
              variant={'caption'}
              color={'text.secondary'}
              fontWeight={'bold'}
            >
              Serial numbers
            </Typography>
          </Grid>
          <Grid item sm={12}>
            <Controller
              name={`parts.${index}.serialNumbers`}
              control={control}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <Grid container gap={1}>
                  {[...Array(Math.min(currentQuantity || 0, 24))].map(
                    (_, idx) => (
                      <Grid container alignItems="center" key={idx} spacing={2}>
                        <Grid item sm={11}>
                          <TextField
                            value={value?.[idx] ?? ''}
                            onChange={(e) => {
                              const newValue = [...(value || [])];
                              newValue[idx] = e.target.value;
                              onChange(newValue);
                            }}
                            size="small"
                            label={`Serial number ${idx + 1}`}
                            fullWidth
                            sx={{ my: 0.5 }}
                          />
                        </Grid>
                        {value?.[idx] &&
                          value.filter((serial) => serial === value[idx])
                            .length > 1 && (
                            <Grid
                              item
                              sm={1}
                              sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                              <Tooltip
                                arrow
                                title={'This overlaps with another serial'}
                              >
                                <WarningAmberOutlined
                                  fontSize="small"
                                  color="warning"
                                />
                              </Tooltip>
                            </Grid>
                          )}
                      </Grid>
                    )
                  )}
                </Grid>
              )}
            />
          </Grid>
          <Grid item sm={12}>
            {currentQuantity >= 25 && (
              <Typography variant="caption">
                For parts with 25 or more serialized components, contact
                support.
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}
